import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ETIQUETAS_ESTADO_STOCK,
  leyendaValorizacion,
  type EstadoStock,
} from '@suminuta/shared';
import MarcoReporte, {
  TD,
  TD_NUM,
  TH,
  TarjetaTotal,
} from '../../components/reportes/MarcoReporte';
import {
  CLASE_INPUT,
  Campo,
  Casilla,
  SelectCategoria,
  SelectProveedor,
} from '../../components/reportes/FiltrosComunes';
import { useExistencias } from '../../hooks/useReportes';
import { formatearPrecio } from '../../utils/facturaCalculos';
import { formatearFechaCon } from '../../utils/dateUtils';
import BloqueLectura from '../../components/reportes/BloqueLectura';
import {
  EtiquetasDestacado,
  claseFila,
  useDestacados,
} from '../../components/reportes/destacados';

/**
 * Reporte 1 — Existencias valorizadas (PROMPT_N°054).
 *
 * Los filtros viven en la query string, igual que en el kardex del N°053:
 * así el reporte se puede compartir por link ya filtrado y el botón
 * Imprimir hereda exactamente lo que se está viendo.
 */

const TONO_ESTADO: Record<EstadoStock, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  bajo: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  critico: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  sin_uso: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
};

export default function ReporteExistencias() {
  const [params, setParams] = useSearchParams();

  const poner = (clave: string, valor: string | boolean) => {
    const p = new URLSearchParams(params);
    const v = String(valor);
    if (v === '' || v === 'undefined') p.delete(clave);
    else p.set(clave, v);
    setParams(p, { replace: true });
  };

  // Los booleanos con default ON se leen como "distinto de false" para que
  // la ausencia del parámetro signifique el default y no "apagado".
  /**
   * Aplica el filtro de una frase clickeable y baja a la tabla. Los filtros
   * viven en la query string (N°053), así que basta con escribirlos ahí:
   * el reporte se vuelve a consultar solo y el link queda compartible.
   */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    setParams(p, { replace: true });
    document
      .getElementById('tabla-reporte')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtros = useMemo(
    () => ({
      categoria_id: params.get('categoria_id') ?? undefined,
      proveedor_id: params.get('proveedor_id') ?? undefined,
      q: params.get('q') ?? undefined,
      en_bodega: params.get('en_bodega') !== 'false',
      con_stock: params.get('con_stock') === 'true',
      incluir_inactivos: params.get('incluir_inactivos') === 'true',
    }),
    [params]
  );

  const { data, isLoading, error } = useExistencias(filtros);
  const filas = data?.filas ?? [];
  const destacados = useDestacados(data?.lectura);

  return (
    <MarcoReporte
      nombre="existencias"
      titulo="Existencias valorizadas"
      descripcion="Qué hay en bodega hoy, en qué estado y cuánto vale."
      filtros={filtros}
      cargando={isLoading}
      error={error}
      vacio={!isLoading && filas.length === 0}
      lectura={
        <BloqueLectura
          lectura={data?.lectura}
          ayuda="existencias"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />
      }
      controles={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SelectCategoria
            valor={params.get('categoria_id') ?? ''}
            onChange={(v) => poner('categoria_id', v)}
          />
          <SelectProveedor
            valor={params.get('proveedor_id') ?? ''}
            onChange={(v) => poner('proveedor_id', v)}
            etiqueta="Proveedor principal"
          />
          <Campo etiqueta="Buscar">
            <input
              className={CLASE_INPUT}
              placeholder="Nombre o marca"
              value={params.get('q') ?? ''}
              onChange={(e) => poner('q', e.target.value)}
            />
          </Campo>
          <div className="space-y-2 md:pt-5">
            <Casilla
              etiqueta="Sólo lo que estuvo en bodega"
              marcada={filtros.en_bodega}
              onChange={(v) => poner('en_bodega', v)}
            />
            <Casilla
              etiqueta="Sólo con stock > 0"
              marcada={filtros.con_stock}
              onChange={(v) => poner('con_stock', v)}
            />
            <Casilla
              etiqueta="Incluir inactivos"
              marcada={filtros.incluir_inactivos}
              onChange={(v) => poner('incluir_inactivos', v)}
            />
          </div>
        </div>
      }
      aviso={
        data && data.sinCosto.length > 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm text-amber-900 dark:text-amber-200">
            <strong>
              El total no incluye {data.sinCosto.length} ingrediente
              {data.sinCosto.length === 1 ? '' : 's'} sin costo cargado:
            </strong>{' '}
            {data.sinCosto.map((s) => s.nombre).join(', ')}.
          </div>
        ) : null
      }
      totales={
        data ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TarjetaTotal
              etiqueta="Ingredientes"
              valor={String(data.total.ingredientes)}
            />
            <TarjetaTotal
              etiqueta="Valor total"
              valor={`$${formatearPrecio(data.total.valor)}`}
              detalle={leyendaValorizacion(data.valorizacion)}
              tono="bueno"
            />
            <TarjetaTotal
              etiqueta="Categorías"
              valor={String(data.porCategoria.length)}
            />
          </div>
        ) : null
      }
    >
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/40">
          <tr>
            <th className={TH}>Ingrediente</th>
            <th className={TH}>Categoría</th>
            <th className={`${TH} text-right`}>Stock</th>
            <th className={`${TH} text-right`}>Mínimo</th>
            <th className={TH}>Estado</th>
            <th className={`${TH} text-right`}>Costo prom. (lotes)</th>
            <th className={`${TH} text-right`}>Costo ref.</th>
            <th className={`${TH} text-right`}>Valor</th>
            <th className={`${TH} text-right`}>Lotes</th>
            <th className={TH}>Próx. vencimiento</th>
            <th className={TH}>Proveedor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {filas.map((f) => (
            <tr
              key={f.id}
              className={
                claseFila(destacados.get(f.id)) ||
                'hover:bg-gray-50 dark:hover:bg-gray-700/40'
              }
            >
              <td className={TD}>
                <div className="font-medium">
                  {f.nombre}
                  <EtiquetasDestacado tipos={destacados.get(f.id)} />
                </div>
                {f.marca && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {f.marca}
                  </div>
                )}
              </td>
              <td className={TD}>{f.categoria ?? 'Sin categoría'}</td>
              <td className={TD_NUM}>
                {f.stock_actual.toLocaleString('es-CL')} {f.unidad}
              </td>
              <td className={TD_NUM}>
                {f.stock_minimo.toLocaleString('es-CL')} {f.unidad}
              </td>
              <td className={TD}>
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TONO_ESTADO[f.estado]}`}
                >
                  {ETIQUETAS_ESTADO_STOCK[f.estado]}
                </span>
              </td>
              <td className={TD_NUM}>
                {f.costo_promedio > 0 ? (
                  `$${f.costo_promedio.toLocaleString('es-CL', { maximumFractionDigits: 2 })}`
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    sin costo
                  </span>
                )}
              </td>
              <td className={`${TD_NUM} text-gray-500 dark:text-gray-400`}>
                {f.costo_referencia
                  ? `$${f.costo_referencia.toLocaleString('es-CL', { maximumFractionDigits: 2 })}`
                  : '—'}
              </td>
              <td className={`${TD_NUM} font-medium`}>
                ${formatearPrecio(f.valor)}
              </td>
              <td className={TD_NUM}>{f.lotes_activos}</td>
              <td className={TD}>
                {f.proximo_vencimiento ? (
                  <>
                    {formatearFechaCon(f.proximo_vencimiento, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      ({f.dias_vencimiento} d)
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className={TD}>{f.proveedor ?? '—'}</td>
            </tr>
          ))}
        </tbody>
        {data && data.porCategoria.length > 0 && (
          <tfoot className="bg-gray-50 dark:bg-gray-900/40 border-t-2 border-gray-300 dark:border-gray-600">
            {data.porCategoria.map((c) => (
              <tr key={c.categoria}>
                <td className={`${TD} font-medium`}>{c.categoria}</td>
                <td className={TD}>{c.ingredientes} ingredientes</td>
                <td className={TD} colSpan={5} />
                <td className={`${TD_NUM} font-medium`}>
                  ${formatearPrecio(c.valor)}
                </td>
                <td className={TD} colSpan={3} />
              </tr>
            ))}
            <tr className="border-t border-gray-300 dark:border-gray-600">
              <td className={`${TD} font-bold`}>TOTAL</td>
              <td className={`${TD} font-bold`}>
                {data.total.ingredientes} ingredientes
              </td>
              <td className={TD} colSpan={5} />
              <td className={`${TD_NUM} font-bold`}>
                ${formatearPrecio(data.total.valor)}
              </td>
              <td className={TD} colSpan={3} />
            </tr>
          </tfoot>
        )}
      </table>
    </MarcoReporte>
  );
}
